/** @jsx jsx */
import React, { type ComponentType, useMemo, type ReactNode, useCallback } from 'react';
import { jsx, cssMap } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { isLoaderErrorAttributes } from '@atlassian/jira-errors-handling/src/utils/is-loader-error-attributes.tsx';
import { PageTopBar } from '@atlassian/jira-horizontal-nav-page-topbar/src/ui/index.tsx';
import { GroupByDropdown as GroupByDropdownItem } from '@atlassian/jira-issue-table-group-by-dropdown/src/index.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useIssueTableSelectedIssuesActions } from '@atlassian/jira-native-issue-table/src/controllers/selected-issues/index.tsx';
import type { topBar_issueNavigator_filter$key as FilterFragment } from '@atlassian/jira-relay/src/__generated__/topBar_issueNavigator_filter.graphql';
import type {
	topBar_issueNavigator_issueResults$key as IssueResultsFragment,
	topBar_issueNavigator_issueResults$data as IssueResultsData,
} from '@atlassian/jira-relay/src/__generated__/topBar_issueNavigator_issueResults.graphql';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { topBar_issueNavigator_jiraViewResult$key as JiraViewResultFragment } from '@atlassian/jira-relay/src/__generated__/topBar_issueNavigator_jiraViewResult.graphql';
import UFOLabel from '@atlassian/jira-ufo-label/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { HideDoneItemsToggle } from '@atlassian/jira-issue-table-hide-done-toggle/src/index.tsx';
import { SaveResetViewSettingsButton } from '@atlassian/jira-issue-navigator-save-reset-view-settings-button/src/ui/SaveResetViewSettingsButton.tsx';
import { FiltersMenu } from '@atlassian/jira-issue-navigator-filters-menu/src/ui/FiltersMenu.tsx';
import type { topBar_issueNavigator_projectResult$key as JiraProjectResultFragment } from '@atlassian/jira-relay/src/__generated__/topBar_issueNavigator_projectResult.graphql';
import { PACKAGE_NAME, TEAM_NAME, isJiraSpreadsheetView } from '../../common/constants.tsx';
import type { CustomHeaderProps } from '../../common/types.tsx';
import { HierarchyToggleItem } from '../../common/ui/hierarchy-toggle/index.tsx';
import { withReportErrors } from '../../common/ui/with-report-errors/index.tsx';
import { useSelectedViewState } from '../../controllers/selected-view-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../../services/active-jql/index.tsx';
import { useFilterQuery } from '../../services/filter-query/index.tsx';
import { useIssueSearchActions } from '../../services/issue-search/selectors.tsx';
import { useUpdateUrlQueryParamCallback } from '../../common/utils/spa-utils.tsx';

export type TopBarProps = {
	ActionMenu: ComponentType<CustomHeaderProps>;
	filter: FilterFragment | null;
	issueResults: IssueResultsFragment | null;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	HeaderSkeletonImage: ComponentType<Record<any, any>>;
	jqlBuilder: ReactNode;
	jiraViewResult: JiraViewResultFragment | null;
	projectResult: JiraProjectResultFragment | null;
	isGroupingSupported?: boolean;
	onChangeJql?: (jql: string, clearFilter?: boolean) => void;
	onChangeFilter?: (filterId?: string) => void;
	isGridNavigationEnabled?: boolean;
	isInlineFieldConfigEnabled?: boolean;
	defaultJql: string;
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
};

const getLastPage = (
	pageCursors: IssueResultsData['pageCursors'] | undefined,
): number | null | undefined => {
	if (pageCursors?.last) {
		return pageCursors.last.pageNumber;
	}

	if (pageCursors?.around?.length) {
		return pageCursors.around[pageCursors.around.length - 1]?.pageNumber;
	}

	return undefined;
};

const styles = cssMap({
	jqlWrapper: {
		'--jql-wrapper-grow': '1',
	},
});

const TopBar = ({
	ActionMenu,
	filter,
	issueResults,
	HeaderSkeletonImage,
	jqlBuilder,
	jiraViewResult,
	projectResult,
	isGroupingSupported,
	onChangeFilter,
	onChangeJql,
	isGridNavigationEnabled,
	isInlineFieldConfigEnabled,
	defaultJql,
	onChangeJqlAndGroupBy,
}: TopBarProps) => {
	const issueResultsData = useFragment<IssueResultsFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment topBar_issueNavigator_issueResults on JiraIssueConnection {
				issueNavigatorPageInfo {
					firstIssuePosition
				}
				pageCursors(maxCursors: 7) {
					around {
						pageNumber
					}
					last {
						pageNumber
					}
				}
				totalIssueSearchResultCount
			}
		`,
		issueResults,
	);

	// Compute page data for our search results which is needed when requesting items for the legacy meatball menu
	const offset = (issueResultsData?.issueNavigatorPageInfo?.firstIssuePosition ?? 1) - 1; // offset is 0 based so subtract 1
	const pages = getLastPage(issueResultsData?.pageCursors) ?? 0;
	const total = issueResultsData?.totalIssueSearchResultCount ?? 0;

	const searchResultPageData = useMemo(
		() => ({
			offset,
			pages,
			total,
		}),
		[offset, pages, total],
	);

	const { filterId } = useActiveJql();
	const jql = useResolvedJql();
	const { filterQueryIsFetching } = useFilterQuery();

	const filterData = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment topBar_issueNavigator_filter on JiraFilter {
				filterId
				name
				isFavourite
				jql
			}
		`,
		filter,
	);

	const projectData = useFragment<JiraProjectResultFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment topBar_issueNavigator_projectResult on JiraProject {
				...FiltersMenu_project
			}
		`,
		projectResult,
	);

	const jiraViewData = useFragment<JiraViewResultFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment topBar_issueNavigator_jiraViewResult on JiraView
			@argumentDefinitions(
				isSavedListViewEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-enabled.relayprovider"
				}
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				__typename
				... on JiraSpreadsheetView {
					...hierarchyToggle_issueNavigator_HierarchyToggleItem
					...GroupByDropdown
					...src_issueTableHideDoneToggle_HideDoneItemsToggle
						@arguments(isSavedViewsEnabled: $isSavedListViewTechnicalChangesEnabled)
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						hideDone
					}
				}
				... on JiraIssueSearchViewMetadata {
					...SaveResetViewSettingsButton @include(if: $isSavedListViewEnabled)
				}
			}
		`,
		jiraViewResult,
	);

	const filterDetails = useMemo(
		() =>
			filterData
				? {
						filterId: filterData.filterId,
						name: filterData.name,
						jql: filterData.jql,
						isFavourite: Boolean(filterData.isFavourite),
					}
				: undefined,
		[filterData],
	);

	const [{ view }] = useSelectedViewState();

	const jiraViewResultData = jiraViewData;

	let changeHierarchy = noop;
	let changeGroupedByField = noop;
	let changeHideDone = noop;
	let resetView = noop;
	if (fg('jsc_m2_hierarchy_fe_changes') || expVal('saved_list_view', 'isEnabled', false)) {
		({ changeHierarchy, changeGroupedByField, changeHideDone, resetView } =
			// eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIssueSearchActions());
	}

	const updateGroupByParam = useUpdateUrlQueryParamCallback('groupBy');
	const onChangeGroupedByField = useCallback(
		(nextGroupedByField: string | null | undefined) => {
			changeGroupedByField(nextGroupedByField);
			updateGroupByParam(nextGroupedByField ?? undefined);
		},
		[changeGroupedByField, updateGroupByParam],
	);

	let hideDone = false;
	if (fg('jsc_bulk_edit_hide_done_work_items')) {
		hideDone = jiraViewData?.viewSettings?.hideDone ?? false;
	}

	const HierarchyToggle =
		jiraViewResultData && view !== 'detail' ? (
			<HierarchyToggleItem
				isGroupingSupported={isGroupingSupported}
				viewResult={jiraViewResultData}
				onChangeHierarchy={changeHierarchy}
			/>
		) : null;

	const GroupByDropdown =
		isGroupingSupported &&
		jiraViewData &&
		isJiraSpreadsheetView(jiraViewData) &&
		view !== 'detail' ? (
			<GroupByDropdownItem
				onChangeGroupedByField={onChangeGroupedByField}
				spreadsheetView={jiraViewData}
			/>
		) : null;

	let onResetSelectedIssues = noop;
	if (fg('jsc_list_hide_done_items')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ reset: onResetSelectedIssues } = useIssueTableSelectedIssuesActions());
	}

	if (filterQueryIsFetching) {
		return <HeaderSkeletonImage />;
	}

	const HideDoneToggle =
		jiraViewResultData && view !== 'detail' && fg('jsc_list_hide_done_items') ? (
			<HideDoneItemsToggle
				isSavedViewsEnabled={fg('saved_list_view_technical_changes')}
				viewResult={jiraViewResultData}
				onChangeHideDone={changeHideDone}
				onResetSelectedIssues={onResetSelectedIssues}
			/>
		) : null;

	const FiltersMenuComponent =
		projectData &&
		onChangeJql &&
		expVal('jira-issue-navigator-saved-filters-menu', 'isEnabled', false) ? (
			<FiltersMenu
				defaultJql={defaultJql}
				onChangeJql={onChangeJql}
				projectFragment={projectData}
			/>
		) : null;

	const saveResetViewSettingsButton = jiraViewResultData &&
		expVal('saved_list_view', 'isEnabled', false) && (
			<SaveResetViewSettingsButton
				jql={jql}
				onReset={resetView}
				viewFragment={jiraViewResultData}
			/>
		);

	const pageTopBar = (
		<PageTopBar
			actions={
				<ActionMenu
					jql={jql}
					filterId={filterId}
					filterDetails={filterDetails}
					searchResultPageData={searchResultPageData}
					HierarchyToggle={HierarchyToggle}
					GroupByDropdown={GroupByDropdown}
					HideDoneToggle={HideDoneToggle}
					FiltersMenu={FiltersMenuComponent}
					SaveResetViewSettingsButton={saveResetViewSettingsButton}
					isHideDoneWorkItemsEnabled={hideDone}
					onChangeFilter={onChangeFilter}
					isModifiedFilter={
						fg('visual-refresh-headers-buttons') ? filterDetails?.jql !== jql : false
					}
					isGridNavigationEnabled={isGridNavigationEnabled}
					isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
					onChangeJqlAndGroupBy={onChangeJqlAndGroupBy}
				/>
			}
			bottomBar={jqlBuilder}
		/>
	);

	if (getWillShowNav4()) {
		return (
			<UFOLabel name="top-bar">
				<div css={styles.jqlWrapper}>{pageTopBar}</div>
			</UFOLabel>
		);
	}
	return <UFOLabel name="top-bar">{pageTopBar}</UFOLabel>;
};

export default withReportErrors<TopBarProps>(TopBar, {
	id: 'ui.topbar.unhandled',
	packageName: PACKAGE_NAME,
	teamName: TEAM_NAME,
	sendToPrivacyUnsafeSplunk: true,
	attributes: isLoaderErrorAttributes,
});
